import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTablePagination } from '@/components/dataTable/Pagination';
import Select from '@/components/dataTable/TableSelect';
import { defaultMeta } from '@/constants/defaultMeta';
import { Button } from '@/design-system/button';
import { Input } from '@/design-system/input';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { SearchIcon } from '@/icons';
import { CsvIcon } from '@/icons/csv-icon';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { referralReportQueries } from '@/services/dashboard/referralReports/referralReports.queries';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';

import { sortBy } from '@/lib/sortBy';
import { mapCustomerReferralReports } from '../../mapCustomerReferralReports';
import { getIndividualCustomerColumns } from './Column';

export function IndividualCustomerReferralTable({ dateRange }: { dateRange?: DateRange }) {
  const canReadReport = useCan({ allOf: [PERMS.referral.customerReport.read] });
  const { t, locale } = useDynamicLocale();
  const { referrerId } = useParams({ from: '/(app)/dashboard/referral-reports/$reportType/$referrerId/' });
  const { q, page, fetch, stageCode, sort } = useSearch({
    from: '/(app)/dashboard/referral-reports/$reportType/$referrerId/',
  });
  const navigate = useNavigate({ from: '/dashboard/referral-reports/$reportType/$referrerId' });
  const { data: response, isLoading } = useQuery({
    ...referralReportQueries.customerList({
      referrerCustomerId: referrerId,
      search: q,
      stageCode,
      fromDateMs: dateRange?.from ? new Date(dateRange.from).setHours(0, 0, 0, 0) : 0,
      toDateMs: dateRange?.to ? new Date(dateRange.to).setHours(23, 59, 59, 999) : 0,
      row: page,
      fetch,
    }),
    enabled: canReadReport,
  });
  const pagination = response?.meta?.pagination ?? defaultMeta;
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const data = React.useMemo(
    () => sortBy(mapCustomerReferralReports(response?.data ?? [], locale), sort),
    [response, locale, sort],
  );
  const columns = React.useMemo(() => getIndividualCustomerColumns(t), [t]);
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.referralReports.tableTittle.referReport'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + rowIndex + 1,
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row) => row.referralId,
    shallow: false,
    clearOnDefault: true,
  });

  const exportKeysByColumn = React.useMemo(
    () => ({
      sn: [t('common.page.tableColumn.sn')],
      reservedBy: [t('common.page.tableColumn.reservedBy')],
      reservationDateTime: [t('common.page.tableColumn.reservationDateTime')],
      stageDetails: [t('common.page.tableColumn.stageDetails')],
      pointsEarned: [t('common.page.tableColumn.pointsEarned')],
    }),
    [t],
  );

  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row }, index) => {
      const reservedBy = row.reservedBy && row.reservedBy !== '-' ? row.reservedBy : row.reservedByNickname;

      return {
        [t('common.page.tableColumn.sn')]: getSerialNumber(table, index),
        [t('common.page.tableColumn.reservedBy')]: reservedBy,
        [t('common.page.tableColumn.reservationDateTime')]: formatDate(row.reservationDateTime, 'YYYY/MM/DD HH:mm:ss'),
        [t('common.page.tableColumn.stageDetails')]: row.stageDetails,
        [t('common.page.tableColumn.pointsEarned')]:
          row.pointsEarned === null ? '-' : `+${formatAmount(row.pointsEarned)}`,
      };
    });

    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, exportKeysByColumn);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'individual-customer-referral-report',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  };

  return (
    <DataTable table={table}>
      <div>
        <div className="flex flex-wrap items-center gap-3 border-b border-border-divider px-4 py-3">
          <h2 className="mr-auto text-md font-bold text-primary">
            {t('dashboard.referralReports.tableTittle.referReport')}
          </h2>
          <div className="flex items-center rounded border border-border-divider px-2">
            <SearchIcon className="text-secondary stroke-1.5" size={20} />
            <Input
              placeholder={t('common.page.search')}
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={(event) => table.setGlobalFilter(event.target.value)}
              className="h-8 w-40 border-0 shadow-none outline-none focus-visible:ring-0 lg:w-56"
            />
          </div>
          <Select
            label={t('common.page.tableColumn.stageDetails')}
            value={stageCode ?? 'all'}
            onValueChange={(value) =>
              void navigate({
                search: (previous) => ({
                  ...previous,
                  stageCode: value === 'all' ? undefined : (value as 'stage_1' | 'stage_2'),
                  page: 0,
                }),
              })
            }
            options={[
              { label: t('common.form.label.all'), value: 'all' },
              { label: t('dashboard.referralReports.tabs.stage1'), value: 'stage_1' },
              { label: t('dashboard.referralReports.tabs.stage2'), value: 'stage_2' },
            ]}
            hideDefaultOption
          />
          <Button type="button" onClick={handleExport}>
            <CsvIcon />
            {t('common.page.table.exportCSV')}
          </Button>
        </div>
        <DataTablePagination table={table} />
      </div>
    </DataTable>
  );
}
